import { useState, useRef, useEffect } from 'react';
import api from '../../api';
import stili from '../../components/competenze/stili';
import { formattaData } from '../../utils/date';
import { usePuoScrivere } from '../../utils/permessi';
import CampoTipoTrasformazione from '../../components/scadenziario/CampoTipoTrasformazione';
import SceltaCcnl from '../../components/scadenziario/SceltaCcnl';
import { regolaDi, mancanti, richiestiConCondizioni, TIPI_CONTRATTO, eIntermittente, ORARI, NON_DEFINITO } from '../../utils/campiScadenza';

const nomeMese = (mese) => {
  const [a, m] = mese.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
};
// Proroghe e trasformazioni: il mese di elaborazione proposto è quello della Data pratica (invio) e va verificato dall'operatore
const CATEGORIE_MESE_PRATICA = ['proroga', 'trasformazione'];
// Mese di elaborazione proposto: per proroghe e trasformazioni la Data pratica; per le altre il campo Dal se compilato, altrimenti la Data pratica
const meseProposto = (b, chiave) => (((CATEGORIE_MESE_PRATICA.includes(chiave) ? b.data_evento : (b.data_dal || b.data_evento)) || '').slice(0, 7));
const chiaveCategoria = (tipi, b) => tipi.categorie.find(c => String(c.id) === String(b.categoria_id))?.chiave || b.categoria_chiave;
const DA_CONTROLLARE = { background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '0.4rem 0.6rem' };
const AZIONI_INT = { crea: 'creare il lavoratore in Intermittenti', aggiorna: 'aggiornare il lavoratore già presente in Intermittenti', proroga: 'prorogare il lavoratore in Intermittenti (aggiorna la data di termine del contratto)' };

// Area di caricamento dei documenti UNILAV (solo PDF, Veneto e Friuli Venezia Giulia), revisione di ciascuno e conferma.
// Il PDF non viene conservato: dopo la lettura restano solo i dati strutturati.
// L'XML non si carica più da qui: l'operatrice lavora con i PDF (l'XML arriva per mail dopo e non si usa).
// I lettori XML restano nel backend ma non sono raggiungibili dall'interfaccia.
const NOMI_CAMPO = { tipologia_contrattuale: 'Tipologia contrattuale', causa_cessazione: 'Causa di cessazione', trasformazione: 'Trasformazione', qualifica: 'Qualifica' };
const dataIt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');

export const CONFERMA_SCADENZA = 'conferma della scadenza precedente';
export const VERIFICA_MESE = 'Verifica del mese di elaborazione';
const DUBBIO_FINE_PRORO = /^Data inizio del rapporto non indicata nel PDF/;   // proposta della scadenza precedente (solo se la data inizio manca nel PDF)

// Campi mancanti di una voce in revisione: quelli della categoria più, se l'app ha proposto la scadenza precedente
// (proroga senza fine del contratto nel PDF), la conferma dell'operatore
export function mancantiVoce(tipi, bozza) {
  const tipoVar = tipi.tipi.find(t => t.chiave === 'variazione_paghe');
  const m = mancanti(tipi, regolaDi(tipi, tipoVar?.id, bozza.categoria_id), bozza);
  if (bozza.tipologia_non_mappata && !bozza.tipo_contratto && !m.includes('Tipologia')) m.push('Tipologia');
  if (bozza.scadenza_precedente && !bozza.scadenza_confermata) m.push(CONFERMA_SCADENZA);
  if (CATEGORIE_MESE_PRATICA.includes(chiaveCategoria(tipi, bozza)) && !bozza.mese_verificato) m.push(VERIFICA_MESE);
  return m;
}

export default function UnilavCarica({ tipi, onFinito }) {
  const puoScrivere = usePuoScrivere('scadenziario');
  const [ccnlElenco, setCcnlElenco] = useState([]);
  const ricaricaCcnl = () => api.get('/scadenze/unilav/ccnl').then(r => setCcnlElenco(r.data.ccnl)).catch(() => {});
  const [fase, setFase] = useState('attesa'); // attesa | lettura | revisione | salvataggio | esiti
  const [trascina, setTrascina] = useState(false);
  const [voci, setVoci] = useState([]);
  const [esiti, setEsiti] = useState([]);
  const [errore, setErrore] = useState('');
  const [aziende, setAziende] = useState([]);
  const [significati, setSignificati] = useState({});
  const input = useRef();

  useEffect(() => {
    ricaricaCcnl();
    api.get('/scadenze/unilav/aziende-intermittenti').then(r => setAziende(r.data)).catch(() => {});
    api.get('/scadenze/unilav/codici').then(r => setSignificati(r.data.significati)).catch(() => {});
  }, []);

  const categoriaPerChiave = chiave => tipi.categorie.find(c => c.chiave === chiave)?.id || '';

  const leggi = async (fileList) => {
    const files = [...fileList].filter(f => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    if (!files.length) return setErrore('Scegli uno o più file PDF');
    if (files.length > 10) return setErrore('Massimo 10 file alla volta');
    setErrore('');
    setFase('lettura');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('file', f));
      const res = await api.post('/scadenze/unilav/anteprima', fd);
      setVoci(res.data.risultati.map(r => (r.esito === 'ok' ? {
        ...r, includi: true,
        bozza: { ...r.bozza, categoria_id: categoriaPerChiave(r.bozza.categoria_chiave), aggiorna_coppia: false },
        int: { esegui: false, azienda_id: r.intermittenti.azienda?.id || '', crea_azienda: false, anagrafica_id: '' },
        intermittenti: r.intermittenti,
        proposte: (r.proposte_codici || []).map(p => ({ ...p, includi: p.campo === 'qualifica' })),
      } : r)));
      setFase('revisione');
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Lettura non riuscita');
      setFase('attesa');
    }
  };

  // Il mese segue Dal / Data pratica finché non lo si cambia a mano
  const modifica = (i, campo, valore) => setVoci(v => v.map((x, j) => {
    if (j !== i) return x;
    const bozza = { ...x.bozza, [campo]: valore };
    const chiave = chiaveCategoria(tipi, bozza);
    if (['data_evento', 'data_dal', 'categoria_id'].includes(campo) && !x.meseManuale && meseProposto(bozza, chiave)) bozza.mese_elaborazione = meseProposto(bozza, chiave);
    // proroghe e trasformazioni: se il mese cambia (a mano o perché cambia la proposta) la verifica va rifatta
    if (bozza.mese_elaborazione !== x.bozza.mese_elaborazione) bozza.mese_verificato = false;
    return { ...x, bozza, ...(campo === 'mese_elaborazione' ? { meseManuale: true } : {}) };
  }));
  const impostaVoce = (i, patch) => setVoci(v => v.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const impostaProposta = (i, k, patch) => setVoci(v => v.map((x, j) => (j === i ? { ...x, proposte: x.proposte.map((p, h) => (h === k ? { ...p, ...patch } : p)) } : x)));
  const impostaInt = (i, patch) => setVoci(v => v.map((x, j) => (j === i ? { ...x, int: { ...x.int, ...patch } } : x)));

  const scegliAzienda = async (i, azienda_id) => {
    const v = voci[i];
    impostaInt(i, { azienda_id, crea_azienda: false, anagrafica_id: '' });
    try {
      const res = await api.post('/scadenze/unilav/intermittenti', { dati_letti: v.dati_letti, firma: v.firma, azienda_id: azienda_id || undefined, tipologia: v.bozza.tipo_contratto });
      impostaVoce(i, { intermittenti: res.data });
    } catch { /* la conferma ricontrolla comunque */ }
  };

  // Il servizio Intermittenti vale solo per le tipologie intermittenti: cambiando tipologia si rivaluta (o si azzera)
  const PROPOSTA_VUOTA = { applicabile: false, azione: null, motivo: null, azienda: null, azienda_creabile: false, lavoratore: null };
  const aggiornaIntermittenti = async (i, tipologia) => {
    const v = voci[i];
    if (!eIntermittente(tipologia)) { impostaVoce(i, { intermittenti: PROPOSTA_VUOTA, int: { ...v.int, esegui: false, crea_azienda: false } }); return; }
    try {
      const res = await api.post('/scadenze/unilav/intermittenti', { dati_letti: v.dati_letti, firma: v.firma, azienda_id: v.int.azienda_id || undefined, tipologia });
      impostaVoce(i, { intermittenti: res.data });
    } catch { /* la conferma ricontrolla comunque */ }
  };

  const conferma = async () => {
    const scelte = voci.filter(v => v.esito === 'ok' && v.includi);
    if (!scelte.length) return setErrore('Nessun documento selezionato');
    // I campi richiesti dalla categoria devono essere compilati (il server rifiuta comunque)
    for (const v of scelte) {
      const m = mancantiVoce(tipi, v.bozza);
      if (m.length) return setErrore(`${v.nome_file}: mancano ${m.join(', ')}`);
    }
    setErrore('');
    setFase('salvataggio');
    try {
      const esitiLivelli = [];
      // Prima le descrizioni dei codici scelte nella revisione: il ricalcolo dei dubbi alla conferma le userà
      const proposteCodici = scelte.flatMap(v => (v.proposte || []).filter(p => p.includi && p.significato).map(p => ({ ...p, nome_file: v.nome_file })));
      if (proposteCodici.length) {
        const r = await api.post('/scadenze/unilav/codici/conferma-proposte', { proposte: proposteCodici.map(({ nome_file, includi, ...p }) => p) });
        r.data.esiti.forEach((e, i) => esitiLivelli.push({ nome_file: proposteCodici[i].nome_file, ...e, esito: e.esito === 'salvato' ? 'codice_salvato' : e.esito }));
      }
      if (!scelte.length) {
        setEsiti([...esitiLivelli, ...voci.filter(v => v.esito !== 'ok').map(v => ({ nome_file: v.nome_file, esito: v.esito, messaggio: v.messaggio }))]);
        return setFase('esiti');
      }
      const res = await api.post('/scadenze/unilav/conferma', {
        voci: scelte.map(v => ({
          nome_file: v.nome_file, dati_letti: v.dati_letti, firma: v.firma,
          bozza: { ...v.bozza, ditta_nuova: v.bozza.cliente_id ? null : v.bozza.ditta_nuova },
          intermittenti: v.int.esegui ? {
            esegui: true, azienda_id: v.int.azienda_id || undefined, crea_azienda: v.int.crea_azienda, anagrafica_id: v.int.anagrafica_id || undefined,
            lavoratore_version: v.intermittenti.lavoratore?.version,
          } : {},
        })),
      });
      const altri = voci.filter(v => v.esito !== 'ok').map(v => ({ nome_file: v.nome_file, esito: v.esito, messaggio: v.messaggio }));
      const saltati = voci.filter(v => v.esito === 'ok' && !v.includi).map(v => ({ nome_file: v.nome_file, esito: 'saltato' }));
      const prima = [...esitiLivelli, ...altri, ...saltati];
      setEsiti([...res.data.esiti, ...prima]);
      setFase('esiti');
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Salvataggio non riuscito');
      setFase('revisione');
    }
  };

  // meseScelto: pulsante "Vai a <mese>"; senza, si va al mese della prima riga salvata
  const chiudi = (meseScelto) => {
    const salvate = esiti.filter(e => e.esito === 'salvato' && e.id).map(e => ({ id: e.id, mese: e.mese_riferimento }));
    setFase('attesa'); setVoci([]); setEsiti([]); setErrore('');
    onFinito(salvate, meseScelto);
  };

  if (fase === 'attesa' || fase === 'lettura') {
    return (
      <div className="noStampa" style={{ ...pagina.upload, ...(trascina ? pagina.uploadAttivo : {}) }}
        onDragOver={e => { e.preventDefault(); setTrascina(true); }} onDragLeave={() => setTrascina(false)}
        onDrop={e => { e.preventDefault(); setTrascina(false); leggi(e.dataTransfer.files); }}>
        <strong>Caricamento documenti UNILAV (Veneto e Friuli Venezia Giulia)</strong>
        <span>{fase === 'lettura' ? 'Lettura in corso…' : 'Trascina qui i PDF (massimo 10 file, 5 MB ciascuno) oppure '}
          {fase === 'attesa' && <button type="button" onClick={() => input.current.click()} style={pagina.link}>scegli i file</button>}
        </span>
        <span style={{ fontSize: '0.78rem' }}>Il PDF non viene conservato: dopo la lettura restano solo i dati, che rivedi prima di salvare.</span>
        <input ref={input} type="file" accept=".pdf,application/pdf" multiple style={{ display: 'none' }}
          onChange={e => { leggi(e.target.files); e.target.value = ''; }} />
        {errore && <div style={stili.errore}>{errore}</div>}
      </div>
    );
  }

  if (fase === 'esiti') {
    // Riepilogo: quante righe sono state create e in quale mese sono finite
    const salvateOra = esiti.filter(e => e.esito === 'salvato' && e.id);
    const perMese = {};
    salvateOra.forEach(e => { perMese[e.mese_riferimento] = (perMese[e.mese_riferimento] || 0) + 1; });
    const mesiOrdinati = Object.keys(perMese).sort();
    return (
      <div style={pagina.pannello} className="noStampa">
        <strong>Esito del caricamento</strong>
        {salvateOra.length > 0 ? (
          <div role="status" style={{ ...pagina.esito, ...pagina.esitoOk }}>
            <strong>{salvateOra.length} {salvateOra.length === 1 ? 'riga creata' : 'righe create'}</strong>
            {mesiOrdinati.length === 1 ? ` nel mese di ${nomeMese(mesiOrdinati[0])}.` : `: ${mesiOrdinati.map(m => `${perMese[m]} nel mese di ${nomeMese(m)}`).join(', ')}.`}
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {mesiOrdinati.map(m => <button key={m} type="button" onClick={() => chiudi(m)} style={stili.btnSmall}>Vai a {nomeMese(m)}</button>)}
            </div>
          </div>
        ) : (
          <div role="status" style={{ ...pagina.esito, ...pagina.esitoKo }}>Nessuna riga creata.</div>
        )}
        {esiti.map((e, i) => (
          <div key={i} style={{ ...pagina.esito, ...(['salvato', 'codice_salvato'].includes(e.esito) ? pagina.esitoOk : pagina.esitoKo) }}>
            <strong>{e.nome_file}</strong>: {e.esito === 'codice_salvato' ? `descrizione salvata: ${NOMI_CAMPO[e.campo] || e.campo} ${e.codice}` : e.esito === 'salvato'
              ? `salvata nel mese di ${e.mese_riferimento ? nomeMese(e.mese_riferimento) : '—'}${e.ditta_creata ? ' (azienda creata)' : ''}${e.intermittenti ? ` · Intermittenti: ${e.intermittenti.azione}` : ''}${e.livello_coppia?.esito === 'salvata' ? ` · corrispondenza salvata (CCNL ${e.livello_coppia.ccnl_codice}, codice livello ${e.livello_coppia.livello_codice} → ${e.livello_coppia.livello})` : e.livello_coppia?.esito === 'aggiornata' ? ' · corrispondenza aggiornata' : ''}`
              : e.esito === 'duplicato' ? `già caricato, non è stato salvato niente${e.messaggio ? ` (${e.messaggio})` : ''}`
                : e.esito === 'saltato' ? 'non selezionato, non è stato salvato niente'
                  : `non salvato: ${e.messaggio || 'errore'}`}
          </div>
        ))}
        <div><button onClick={() => chiudi()} style={stili.btn}>Chiudi</button></div>
      </div>
    );
  }

  return (
    <div style={pagina.pannello} className="noStampa">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
        <strong>Revisione dei documenti letti</strong>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>Per ogni PDF si salva tutto o niente. Controlla e correggi i campi, poi conferma.</span>
      </div>
      {errore && <div style={stili.errore}>{errore}</div>}
      {voci.map((v, i) => (v.esito === 'ok'
        ? <Revisione key={i} v={v} i={i} tipi={tipi} aziende={aziende} modifica={modifica} impostaVoce={impostaVoce} impostaInt={impostaInt} ccnlElenco={ccnlElenco} ricaricaCcnl={ricaricaCcnl} puoScrivere={puoScrivere} scegliAzienda={scegliAzienda} aggiornaIntermittenti={aggiornaIntermittenti} significati={significati} impostaProposta={impostaProposta} />
        : (
          <div key={i} style={{ ...pagina.esito, ...pagina.esitoKo }}>
            <strong>{v.nome_file}</strong>: {v.esito === 'duplicato'
              ? `già caricato${v.duplicato?.nel_lotto ? ' (stesso protocollo in questo invio)' : ` (${v.duplicato?.ditta || 'azienda'}: ${[v.duplicato?.categoria, v.duplicato?.lavoratore].filter(Boolean).join(' · ')}, inserito da ${v.duplicato?.inserita_da || '—'})`}. Non verrà creato niente.`
              : v.messaggio}
          </div>
        )))}
      <div style={stili.azioni}>
        <button onClick={chiudi} style={stili.btnSecondary}>Annulla</button>
        <button onClick={conferma} disabled={fase === 'salvataggio' || !voci.some(v => v.esito === 'ok' && v.includi) || voci.some(v => v.esito === 'ok' && v.includi && mancantiVoce(tipi, v.bozza).length > 0)} style={stili.btn}>
          {fase === 'salvataggio' ? 'Salvataggio…' : 'Conferma e salva'}
        </button>
      </div>
    </div>
  );
}

function Revisione({ v, i, tipi, aziende, modifica, impostaVoce, impostaInt, ccnlElenco, ricaricaCcnl, puoScrivere, scegliAzienda, aggiornaIntermittenti, significati, impostaProposta }) {
  const b = v.bozza;
  const int = v.intermittenti;
  const tipoVariazioni = tipi.tipi.find(t => t.chiave === 'variazione_paghe');
  const categorie = tipi.categorie.filter(c => c.attiva && (!tipoVariazioni || c.tipo_id === tipoVariazioni.id));
  // Date richieste dalla categoria e ancora vuote: evidenziate; senza, il salvataggio non è possibile
  const campoData = (testo, nome) => {
    const da = richiesti.has(nome) && !b[nome];
    return (
      <div style={{ ...stili.campo, ...(da ? DA_CONTROLLARE : {}) }}>
        <label style={stili.label} htmlFor={`${nome}-${i}`}>{testo}{richiesti.has(nome) ? ' *' : ''}</label>
        <input id={`${nome}-${i}`} type="date" value={b[nome] || ''} onChange={e => modifica(i, nome, e.target.value)} style={stili.input} />
      </div>
    );
  };
  const regola = regolaDi(tipi, tipoVariazioni?.id, b.categoria_id);
  const mostra = regola.mostra;
  const richiesti = richiestiConCondizioni(regola, b);
  const mancano = mancantiVoce(tipi, b);
  // Spuntata la conferma della scadenza precedente, il relativo dubbio non si mostra più (gli altri restano)
  const dubbiVisibili = b.scadenza_confermata ? b.dubbi.filter(d => !DUBBIO_FINE_PRORO.test(d)) : b.dubbi;
  const etichetta = (testo, nome) => `${testo}${richiesti.has(nome) ? ' *' : ''}`;
  const campo = (etichetta, nome, tipo = 'text', extra = {}) => (
    <div style={stili.campo}>
      <label style={stili.label} htmlFor={`${nome}-${i}`}>{etichetta}</label>
      <input id={`${nome}-${i}`} type={tipo} value={b[nome] || ''} onChange={e => modifica(i, nome, e.target.value)} style={stili.input} {...extra} />
    </div>
  );
  const rigaOre = mostra.has('tipo_trasformazione');   // trasformazione: Tipo contratto, Ore settimanali e Tipo trasformazione nella stessa riga
  const intermittente = eIntermittente(b.tipo_contratto);
  const orarioCampo = mostra.has('orario') && (
    <div style={stili.campo}>
      <label style={stili.label} htmlFor={`orario-${i}`}>{etichetta('Orario', 'orario')}</label>
      <select id={`orario-${i}`} value={intermittente ? '' : (b.orario || '')} disabled={intermittente}
        onChange={e => { modifica(i, 'orario', e.target.value); if (e.target.value === 'full_time' && !(Number(b.ore_settimanali) > 0)) modifica(i, 'ore_settimanali', 40); }} style={stili.input}>
        {intermittente ? <option value="">{NON_DEFINITO}</option> : <><option value="">—</option><option value="full_time">{ORARI.full_time}</option><option value="part_time">{ORARI.part_time}</option></>}
      </select>
    </div>
  );
  const tipoContratto = (
    <div style={{ ...stili.campo, ...((richiesti.has('tipo_contratto') || b.tipologia_non_mappata) && !b.tipo_contratto ? DA_CONTROLLARE : {}) }}>
              <label style={stili.label} htmlFor={`contratto-${i}`}>{b.tipologia_non_mappata && !richiesti.has('tipo_contratto') ? 'Tipologia *' : etichetta('Tipologia', 'tipo_contratto')}</label>
              <select id={`contratto-${i}`} value={b.tipo_contratto || ''} onChange={e => { modifica(i, 'tipo_contratto', e.target.value); if (eIntermittente(e.target.value)) { modifica(i, 'ore_settimanali', ''); modifica(i, 'orario', ''); } if (e.target.value === 'intermittente_indet') modifica(i, 'data_scadenza', ''); aggiornaIntermittenti(i, e.target.value); }} style={stili.input}>
                <option value="">—</option>
                {Object.entries(TIPI_CONTRATTO).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
              </select>
              {b.tipologia_non_mappata && (
                <small style={{ color: '#9a3412' }}>Nel PDF: «{b.tipologia_non_mappata}». Non corrisponde a nessuna delle cinque tipologie: scegline una.</small>
              )}
            </div>
  );
  return (
    <div style={pagina.scheda}>
      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600, flexWrap: 'wrap', overflowWrap: 'anywhere' }}>
        <input type="checkbox" checked={v.includi} onChange={e => impostaVoce(i, { includi: e.target.checked })} />
        {v.nome_file} <span style={pagina.tag}>{v.dati_letti.tipo_comunicazione}</span>
        <span style={{ fontWeight: 400, color: '#666', fontSize: '0.78rem' }}>codice comunicazione {v.dati_letti.protocollo}</span>
      </label>
      {dubbiVisibili.length > 0 && (
        <ul style={pagina.dubbi}>
          {dubbiVisibili.map((d, k) => <li key={k}>{d}</li>)}
        </ul>
      )}

      {b.scadenza_precedente && (
        <div style={pagina.proposta}>
          <strong>Scadenza precedente della stessa persona</strong>
          <div style={{ fontSize: '0.84rem', margin: '0.2rem 0' }}>
            Nel PDF manca la data di inizio del rapporto. Ho trovato {dataIt(b.scadenza_precedente.data)} ({b.scadenza_precedente.fonte}) e l'ho messa come Dal.
            {b.scadenza_precedente.alternative.map(a => (
              <span key={a.fonte + a.data}> Trovata anche {dataIt(a.data)} ({a.fonte}){' '}
                <button type="button" style={stili.btnSmall} onClick={() => { modifica(i, 'data_dal', a.data); modifica(i, 'scadenza_confermata', false); }}>Usa questa</button>
              </span>
            ))}
          </div>
          <label style={{ fontSize: '0.84rem', ...(b.scadenza_confermata ? {} : { color: '#b91c1c', fontWeight: 600 }) }}>
            <input type="checkbox" checked={Boolean(b.scadenza_confermata)} onChange={e => modifica(i, 'scadenza_confermata', e.target.checked)} /> Confermo la scadenza precedente *
          </label>
        </div>
      )}
      {v.proposte && v.proposte.length > 0 && (
        <div style={pagina.proposta}>
          <strong>Descrizioni dei codici letti dal PDF</strong>
          <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: '0.3rem' }}>Questi codici non sono ancora nelle tabelle. Spunta per salvarli (per causa, trasformazione e tipologia scegli il significato); la descrizione si può correggere.</div>
          {v.proposte.map((p, k) => (
            <div key={p.campo + p.codice} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
              <input type="checkbox" checked={p.includi} aria-label={`Salva ${p.codice}`} onChange={e => impostaProposta(i, k, { includi: e.target.checked })} />
              <span style={{ fontSize: '0.8rem', minWidth: '140px' }}>{NOMI_CAMPO[p.campo]} <strong>{p.codice}</strong></span>
              <input value={p.descrizione} onChange={e => impostaProposta(i, k, { descrizione: e.target.value })} style={{ ...stili.input, flex: 1, minWidth: '200px' }} aria-label={`Descrizione ${p.codice}`} />
              {p.campo !== 'qualifica' && (
                <select value={p.significato || ''} onChange={e => impostaProposta(i, k, { significato: e.target.value || null })} style={{ ...stili.input, width: 'auto' }} aria-label={`Significato ${p.codice}`}>
                  <option value="">Significato…</option>
                  {Object.entries((significati || {})[p.campo] || {}).map(([chiave, nome]) => <option key={chiave} value={chiave}>{nome}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ ...stili.row, alignItems: 'flex-start' }}>
        <div style={{ ...stili.campo, flex: 2, marginBottom: 0 }}>
          <label style={stili.label}>Azienda</label>
          {b.cliente_id && <div style={pagina.valoreFisso}>{b.ditta.ragione_sociale}</div>}
          {!b.cliente_id && b.ditte_candidate.length > 0 && (
            <select value={b.cliente_id || ''} onChange={e => modifica(i, 'cliente_id', Number(e.target.value) || null)} style={stili.input}>
              <option value="">Scegli l'azienda…</option>
              {b.ditte_candidate.map(d => <option key={d.id} value={d.id}>{d.ragione_sociale}</option>)}
            </select>
          )}
          {!b.cliente_id && b.ditte_candidate.length === 0 && b.ditta_nuova && (
            <div style={{ border: '1px solid #fdba74', background: '#fff7ed', borderRadius: '6px', padding: '0.5rem 0.6rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#9a3412', marginBottom: '0.3rem' }}>
                {b.anagrafica_esistente
                  ? `L'azienda è in anagrafica (${b.anagrafica_esistente.ragione_sociale}) ma non ha ancora la scheda di Scadenziario: alla conferma la scheda viene creata e collegata a quella anagrafica.`
                  : 'Azienda nuova: non è né in anagrafica né nello Scadenziario. Alla conferma nascono l\'anagrafica "da completare" e la scheda (puoi correggere i dati letti dal PDF).'}
              </div>
              {(b.forse_e || []).length > 0 && !b.ditta_nuova.anagrafica_id && (
                <div role="status" aria-label="Forse è" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.4rem 0.6rem', margin: '0.3rem 0', fontSize: '0.82rem' }}>
                  <strong>Forse è…</strong> (stesso nome o simile, ma codice fiscale diverso: confermalo tu, non viene collegato in automatico)
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
                    {b.forse_e.map(f => (
                      <li key={f.anagrafica_id}>{f.ragione_sociale}{f.somiglianza === 'uguale' ? ' (stesso nome)' : ''}{' '}
                        <button type="button" style={stili.btnSmall} onClick={() => {
                          if (f.cliente_id) { modifica(i, 'ditta', { ragione_sociale: f.ragione_sociale }); modifica(i, 'cliente_id', f.cliente_id); }
                          else modifica(i, 'ditta_nuova', { ...b.ditta_nuova, anagrafica_id: f.anagrafica_id, anagrafica_nome: f.ragione_sociale });
                        }}>È questa</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {b.ditta_nuova.anagrafica_id ? (
                <div style={{ fontSize: '0.84rem' }} aria-label="Forse è confermato">
                  Confermato a mano: la scheda verrà creata e collegata all'anagrafica <strong>{b.ditta_nuova.anagrafica_nome}</strong>.{' '}
                  <button type="button" style={stili.btnSmall} onClick={() => modifica(i, 'ditta_nuova', { ...b.ditta_nuova, anagrafica_id: undefined, anagrafica_nome: undefined })}>Togli</button>
                </div>
              ) : (
                <>
                  <input value={b.ditta_nuova.ragione_sociale} onChange={e => modifica(i, 'ditta_nuova', { ...b.ditta_nuova, ragione_sociale: e.target.value })} style={stili.input} aria-label="Denominazione della nuova azienda" placeholder="Denominazione" />
                  <input value={b.ditta_nuova.codice_fiscale || ''} onChange={e => modifica(i, 'ditta_nuova', { ...b.ditta_nuova, codice_fiscale: e.target.value, partita_iva: /^\d{11}$/.test(e.target.value.trim()) ? e.target.value.trim() : null })} style={{ ...stili.input, marginTop: '0.3rem', maxWidth: '240px' }} aria-label="Codice fiscale della nuova azienda" placeholder="Codice fiscale" />
                  <select value={b.ditta_nuova.tipo_soggetto || ''} onChange={e => modifica(i, 'ditta_nuova', { ...b.ditta_nuova, tipo_soggetto: e.target.value })} style={{ ...stili.input, marginTop: '0.3rem', maxWidth: '240px' }} aria-label="Tipo di soggetto della nuova azienda">
                    <option value="">Tipo di soggetto</option>
                    <option value="societa_capitali">Società di capitali</option>
                    <option value="societa_persone">Società di persone</option>
                    <option value="ditta_individuale">Ditta individuale</option>
                    <option value="persona_fisica">Persona fisica</option>
                    <option value="altro">Altro</option>
                  </select>
                  {b.ditta_nuova.tipo_soggetto === 'persona_fisica' && String(b.ditta_nuova.codice_fiscale || '').replace(/\s+/g, '').length !== 16 && (
                    <div role="status" style={{ fontSize: '0.8rem', color: '#9a3412', marginTop: '0.3rem' }}>
                      Una persona fisica richiede il codice fiscale di 16 caratteri (senza partita IVA): scrivilo qui, altrimenti la conferma viene rifiutata.
                    </div>
                  )}
                  {b.ditta_nuova.tipo_soggetto === 'ditta_individuale' && String(b.ditta_nuova.codice_fiscale || '').replace(/\s+/g, '').length !== 16 && (
                    <div role="status" style={{ fontSize: '0.8rem', color: '#9a3412', marginTop: '0.3rem' }}>
                      Una ditta individuale è completa solo con il codice fiscale di 16 caratteri: se lo conosci scrivilo qui; altrimenti si salva comunque e l'anagrafica resta "da completare".
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {!b.cliente_id && b.ditte_candidate.length === 0 && !b.ditta_nuova && <div style={pagina.valoreFisso}>—</div>}
        </div>
        <div style={{ ...stili.campo, marginBottom: 0 }}>
          <label style={stili.label}>Categoria</label>
          <select value={b.categoria_id} onChange={e => modifica(i, 'categoria_id', e.target.value)} style={stili.input}>
            {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>
      {!b.cliente_id && b.ditte_candidate.length === 0 && !b.ditta_nuova && (
        <div style={{ ...pagina.notaDitta, color: '#b91c1c' }}>Nel PDF manca il codice fiscale del datore: non si può trovare né creare l'azienda.</div>
      )}

      <div style={{ ...stili.row, marginTop: '0.9rem' }}>
        {campo('Cognome *', 'lavoratore_cognome')}
        {campo('Nome *', 'lavoratore_nome')}
        {mostra.has('matricola') && (
          <div style={{ ...stili.campo, ...(richiesti.has('matricola') ? DA_CONTROLLARE : {}) }}>
            <label style={stili.label}>{etichetta('Matricola', 'matricola')}</label>
            <input value={b.matricola || ''} onChange={e => modifica(i, 'matricola', e.target.value)} style={stili.input} />
            {b.matricola_precedente && (
              <button type="button" style={{ ...stili.btnSmall, marginTop: '0.3rem' }} onClick={() => modifica(i, 'matricola', b.matricola_precedente)}>
                Già in questa ditta con matricola {b.matricola_precedente}: usala
              </button>
            )}
          </div>
        )}
      </div>

      {(mostra.has('tipo_contratto') || mostra.has('livello') || mostra.has('mansione') || mostra.has('ccnl_codice')) && (
        <div style={stili.row}>
          {mostra.has('tipo_contratto') && !rigaOre && tipoContratto}
          {mostra.has('ccnl_codice') && <SceltaCcnl id={`ccnl-${i}`} valore={b.ccnl_codice} onCambia={c => modifica(i, 'ccnl_codice', c)} letto={b.ccnl_letto} elenco={ccnlElenco} ricarica={ricaricaCcnl} puoScrivere={puoScrivere} obbligatorio={richiesti.has('ccnl_codice')} />}
          {mostra.has('livello') && (
            <div style={{ ...stili.campo, ...DA_CONTROLLARE }}>
              <label style={stili.label} htmlFor={`livello-${i}`}>{etichetta('Livello', 'livello')}</label>
              <input id={`livello-${i}`} value={b.livello || ''} maxLength={60} placeholder="es. 6, IV, Quadro" onChange={e => modifica(i, 'livello', e.target.value)} style={stili.input} />
              {b.livello_info && Boolean(b.livello_info.livello) && String(b.livello || '').trim() !== '' && String(b.livello).trim().toLowerCase() !== b.livello_info.livello.trim().toLowerCase() && (
                <label style={{ display: 'block', fontSize: '0.82rem', marginTop: '0.3rem' }}>
                  <input type="checkbox" checked={Boolean(b.aggiorna_coppia)} onChange={e => modifica(i, 'aggiorna_coppia', e.target.checked)} /> Aggiorna la corrispondenza anche per le prossime volte
                  <span style={{ color: '#777' }}> (se non la spunti, il valore vale solo per questa riga)</span>
                </label>
              )}
            </div>
          )}
          {mostra.has('mansione') && (
            <div style={{ ...stili.campo, ...(richiesti.has('mansione') && !String(b.mansione || '').trim() ? DA_CONTROLLARE : {}) }}>
              <label style={stili.label} htmlFor={`mansione-${i}`}>{etichetta('Mansione', 'mansione')}</label>
              <input id={`mansione-${i}`} value={b.mansione || ''} maxLength={200} placeholder="es. cuoco, cameriere" onChange={e => modifica(i, 'mansione', e.target.value)} style={stili.input} />
            </div>
          )}
        </div>
      )}

      {(mostra.has('stagionale') || mostra.has('ore_settimanali')) && (
        <div style={stili.row}>
          {rigaOre && tipoContratto}
          {mostra.has('stagionale') && (
            <div style={{ ...stili.campo, ...(richiesti.has('stagionale') && b.stagionale !== true && b.stagionale !== false ? DA_CONTROLLARE : {}) }}>
              <label style={stili.label} htmlFor={`stagionale-${i}`}>{etichetta('Stagionale', 'stagionale')}</label>
              <select id={`stagionale-${i}`} value={b.stagionale === true ? 'si' : b.stagionale === false ? 'no' : ''} onChange={e => modifica(i, 'stagionale', e.target.value === 'si' ? true : e.target.value === 'no' ? false : null)} style={stili.input}>
                <option value="">—</option>
                <option value="si">Sì</option>
                <option value="no">No</option>
              </select>
            </div>
          )}
          {orarioCampo}
          {mostra.has('ore_settimanali') && (
            <div style={{ ...stili.campo, ...(richiesti.has('ore_settimanali') && !(Number(b.ore_settimanali) > 0) ? DA_CONTROLLARE : {}) }}>
              <label style={stili.label} htmlFor={`ore-${i}`}>{etichetta('Ore settimanali', 'ore_settimanali')}</label>
              <input id={`ore-${i}`} type={intermittente ? 'text' : 'number'} min="0" step="0.5" disabled={intermittente} value={intermittente ? NON_DEFINITO : (b.ore_settimanali ?? '')} onChange={e => modifica(i, 'ore_settimanali', e.target.value === '' ? '' : Number(e.target.value))} style={stili.input} />
              {b.percentuale_part_time && Number(b.ore_settimanali) > 0 && <small style={{ color: '#777' }}>= {Math.round((Number(b.ore_settimanali) / 40) * 1000) / 10}%</small>}
            </div>
          )}
          {mostra.has('tipo_trasformazione') && (
            <CampoTipoTrasformazione id={`trasf-${i}`} valore={b.tipo_trasformazione} onCambia={v => modifica(i, 'tipo_trasformazione', v)}
              etichetta={etichetta('Tipo di trasformazione', 'tipo_trasformazione')} daControllare={richiesti.has('tipo_trasformazione') && !String(b.tipo_trasformazione || '').trim()}
              avviso={b.tipo_trasformazione_non_mappato && !String(b.tipo_trasformazione || '').trim() ? `Nel PDF: «${b.tipo_trasformazione_non_mappato}». Non corrisponde a nessuna voce dell'elenco: scegline una.` : ''} />
          )}
        </div>
      )}

      <div style={stili.row}>
        {/* Cessazioni e trasformazioni: Data cessazione / Data trasformazione (Dal) prima della Data pratica (= invio), tutte nella stessa riga */}
        {mostra.has('data_dal') && regola.etichette.data_dal && campoData(regola.etichette.data_dal, 'data_dal')}
        {campoData('Data pratica', 'data_evento')}
        {mostra.has('data_dal') && !regola.etichette.data_dal && campoData('Dal', 'data_dal')}
        {mostra.has('data_scadenza') && campoData('Data scadenza', 'data_scadenza')}
        {campo('Mese di elaborazione', 'mese_elaborazione', 'month')}
      </div>
      {CATEGORIE_MESE_PRATICA.includes(chiaveCategoria(tipi, b)) && (
        <div style={{ ...DA_CONTROLLARE, margin: '0.2rem 0 0.6rem' }}>
          <div style={{ fontSize: '0.84rem', color: '#9a3412', fontWeight: 600 }}>
            Controlla il mese di elaborazione: la pratica verrà inserita nel mese di {b.mese_elaborazione ? nomeMese(b.mese_elaborazione) : '—'}.
            Se il contratto scade a fine mese o la pratica va lavorata in un altro mese, cambialo.
          </div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.35rem' }}>
            <input type="checkbox" checked={Boolean(b.mese_verificato)} onChange={e => modifica(i, 'mese_verificato', e.target.checked)} /> Ho verificato il mese di elaborazione *
          </label>
        </div>
      )}
      {campo('Note', 'note')}
      {v.includi && mancano.length > 0 && <div role="alert" style={{ color: '#b91c1c', fontSize: '0.84rem', margin: '0 0 0.5rem' }}>Mancano: {mancano.join(', ')}. Compilali per poter salvare questo documento.</div>}
      <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', color: '#777' }}>
        Dai documenti: invio {v.dati_letti.data_invio ? `${formattaData(v.dati_letti.data_invio.slice(0, 10))}${v.dati_letti.data_invio.slice(11) && v.dati_letti.data_invio.slice(11) !== '00:00:00' ? ` ${v.dati_letti.data_invio.slice(11)}` : ''}` : '—'}.
      </p>

      {eIntermittente(b.tipo_contratto) && (int.applicabile || int.motivo) && (
        <div style={pagina.int}>
          <strong style={{ fontSize: '0.85rem' }}>Servizio Intermittenti</strong>
          {int.applicabile ? (
            <>
              <label style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.3rem' }}>
                <input type="checkbox" checked={v.int.esegui} onChange={e => impostaInt(i, { esegui: e.target.checked })} /> Voglio {AZIONI_INT[int.azione]}. Non parte nessuna email.
              </label>
              {!int.azienda && v.int.esegui && (
                <div style={{ marginTop: '0.4rem' }}>
                  <select aria-label="Azienda di Intermittenti" value={v.int.anagrafica_id ? `forse:${v.int.anagrafica_id}` : (v.int.crea_azienda ? 'nuova' : (v.int.azienda_id || ''))} style={stili.input}
                    onChange={e => {
                      const val = e.target.value;
                      if (val === 'nuova') impostaInt(i, { crea_azienda: true, azienda_id: '', anagrafica_id: '' });
                      else if (val.startsWith('forse:')) impostaInt(i, { crea_azienda: true, azienda_id: '', anagrafica_id: Number(val.slice(6)) });
                      else scegliAzienda(i, val);
                    }}>
                    <option value="">Scegli l'azienda di Intermittenti…</option>
                    {aziende.map(a => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                    <option value="nuova">{int.caso_azienda === 'in_anagrafica_senza_scheda' && int.anagrafica
                      ? `Crea la scheda di Intermittenti per ${int.anagrafica.ragione_sociale} (collegata all'anagrafica)`
                      : `Crea l'azienda ${v.dati_letti.datore.denominazione || ''} (anagrafica "da completare" e scheda)`}</option>
                    {(int.simili || []).map(f => <option key={f.anagrafica_id} value={`forse:${f.anagrafica_id}`}>Forse è: {f.ragione_sociale}{f.azienda_id ? ' (ha già la scheda)' : ''}</option>)}
                  </select>
                </div>
              )}
              {int.azienda && <div style={{ fontSize: '0.78rem', color: '#666' }}>Azienda: {int.azienda.ragione_sociale}</div>}
              {!int.azienda && int.motivo && <div style={{ fontSize: '0.8rem', color: '#9a3412', marginTop: '0.3rem' }}>{int.motivo}</div>}
            </>
          ) : <div style={{ fontSize: '0.82rem', color: '#9a3412' }}>{int.motivo}</div>}
        </div>
      )}
    </div>
  );
}

const pagina = {
  valoreFisso: { padding: '0.5rem 0', fontSize: '0.9rem', lineHeight: 1.3, minHeight: '1.2rem' },
  notaDitta: { fontSize: '0.8rem', color: '#777', marginTop: '0.3rem' },
  upload: { display: 'flex', flexDirection: 'column', gap: '0.25rem', border: '2px dashed #D3D678', borderRadius: '10px', padding: '1rem', marginBottom: '1rem', color: '#444', background: '#fafaf8', fontSize: '0.88rem' },
  uploadAttivo: { background: '#F7F7EA', borderColor: '#232323' },
  link: { background: 'none', border: 'none', padding: 0, textDecoration: 'underline', cursor: 'pointer', color: '#232323', fontSize: '0.88rem' },
  pannello: { border: '1px solid #ddd', borderRadius: '10px', padding: '1rem', marginBottom: '1rem', background: 'white', display: 'flex', flexDirection: 'column', gap: '0.7rem' },
  scheda: { border: '1px solid #e5e5df', borderRadius: '8px', padding: '0.8rem', background: '#fcfcfa' },
  tag: { fontSize: '0.7rem', background: '#232323', color: 'white', borderRadius: '4px', padding: '0.05rem 0.4rem' },
  differenze: { background: '#fef3c7', color: '#92400e', borderRadius: '6px', padding: '0.5rem 0.7rem', fontSize: '0.82rem', margin: '0.5rem 0' },
  proposta: { border: '1px solid #D3D678', background: '#F7F7EA', borderRadius: '6px', padding: '0.5rem 0.7rem', margin: '0.5rem 0 0.8rem' },
  dubbi: { background: '#fff7ed', color: '#9a3412', borderRadius: '6px', padding: '0.5rem 0.5rem 0.5rem 1.6rem', fontSize: '0.82rem', margin: '0.5rem 0' },
  int: { border: '1px dashed #bbb', borderRadius: '6px', padding: '0.5rem 0.7rem', marginTop: '0.4rem' },
  esito: { borderRadius: '6px', padding: '0.5rem 0.7rem', fontSize: '0.86rem' },
  esitoOk: { background: '#ecfdf5', color: '#166534' },
  esitoKo: { background: '#fef2f2', color: '#b91c1c' },
};
